import React, { useEffect, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    TextField,
    InputAdornment,
    MenuItem,
    DialogActions,
    IconButton,
} from "@mui/material";
import { useForm, Controller, useWatch } from "react-hook-form";
import { useSnackbar } from 'notistack';
import { postReq, getReq } from "../../../apis/api";
import CategoryIcon from "@mui/icons-material/Category";
import CloseIcon from "@mui/icons-material/Close";
import FormButton from "../../Button";
import { todayIndia } from "../../../utils/attention";

// SGEW 19 Oct (field audit): the sale date is sent as `date` and the server now reads it (it read `schedule`, so every
// sale was dated today); the weight is the chosen rows' scrap weight (the server works it out again); the amount must
// be above 0 and the date not after today. After a sale both lists reload; the server's reason is shown when it refuses.
// SGEW 19 Oct (owner Q7): the buyer (free text, optional, 150 characters) and how it was paid - Cash or Online (required).
const kg = (v) => Math.round((Number(v) || 0) * 100) / 100;

const SellScrapModal = ({ open, onClose, onItemAdded = () => {} }) => {
    const {
        handleSubmit,
        control,
        reset,
        setValue,
        formState: { errors },
    } = useForm({
        defaultValues: {
            scrap_item: [],
            weight: "",
            amount: "",
            date: todayIndia(), // usually sold the same day; any earlier day can be picked
            buyer_name: "",
            payment_mode: "",
        },
    });

    const [categories, setCategories] = useState([]);
    const [fetchingCategories, setFetchingCategories] = useState(false);
    const { enqueueSnackbar } = useSnackbar();

    const selectedScrapItems = useWatch({
        control,
        name: "scrap_item",
    });

    const fetchCategories = async () => {
        try {
            setFetchingCategories(true);
            const response = await getReq("fetchScrap");
            if (response?.status) {
                setCategories(response.data);
            }
        } catch (error) {
            setCategories([]);
            // 404 = no open scrap: an empty list, not an error
            if (error?.response?.status !== 404) enqueueSnackbar("The scrap list could not be loaded.", { variant: "error" });
        } finally {
            setFetchingCategories(false);
        }
    };

    useEffect(() => {
        if (open) {
            fetchCategories();
            reset(); // reset form when modal opens
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- loads and resets only when the modal opens
    }, [open]);

    // Auto calculate weight when scrap_item changes
    useEffect(() => {
        if (!selectedScrapItems || selectedScrapItems.length === 0) {
            setValue("weight", "");
            return;
        }

        const totalWeight = categories
            .filter((cat) => selectedScrapItems.includes(cat.id))
            .reduce((sum, cat) => sum + (parseFloat(cat.scrap_weight) || 0), 0);

        setValue("weight", kg(totalWeight)); // 0.1 + 0.2 showed 0.30000000000000004
    }, [selectedScrapItems, categories, setValue]);

    const onSubmit = async (data) => {
        try {
            // const payload = {
            //     ...data,
            //     scrap_item: data.scrap_item.join(","), // convert array to comma-separated
            // };

            const res = await postReq(
                {
                    scrap_item: data.scrap_item.join(","),
                    amount: data.amount,
                    date: data.date,
                    buyer_name: (data.buyer_name || "").trim(),
                    payment_mode: data.payment_mode,
                },
                "/ScrapSell"
            );
            enqueueSnackbar(res?.data?.message || "Scrap sold.", { variant: "success", autoHideDuration: 8000 });
            reset();
            onItemAdded();
            onClose();
        } catch (error) {
            const body = error?.response?.data || {};
            const said = body.message || (body.messages && Object.values(body.messages).join(" "));
            if (error?.response?.status !== 403 && !(error?.response?.status >= 500)) {
                // 403 / 5xx are reported once by the session interceptor
                enqueueSnackbar(said || "The scrap sale could not be saved.", { variant: "error" });
            }
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="sm"
            sx={{
                "& .MuiDialogContent-root": {
                    padding: "24px !important",
                },
            }}
        >
            <DialogTitle sx={{ m: 0, p: 2 }}>
                Sell Scrap
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{
                        position: "absolute",
                        right: 8,
                        top: 8,
                        color: "#6b7280",
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                <form>
                    {/* Scrap Category (Multi-select) */}
                    <Controller
                        name="scrap_item"
                        control={control}
                        rules={{ required: "Please select at least one category" }}
                        render={({ field }) => {
                            const isAllSelected =
                                categories.length > 0 && field.value?.length === categories.length;

                            const handleChange = (event) => {
                                const {
                                    target: { value },
                                } = event;

                                if (value[value.length - 1] === "all") {
                                    field.onChange(
                                        isAllSelected ? [] : categories.map((c) => c.id)
                                    );
                                } else {
                                    field.onChange(value);
                                }
                            };

                            return (
                                <TextField
                                    {...field}
                                    select
                                    label="Scrap to sell"
                                    fullWidth
                                    disabled={fetchingCategories}
                                    SelectProps={{
                                        multiple: true,
                                        value: field.value || [],
                                        onChange: handleChange,
                                        renderValue: (selected) =>
                                            categories
                                                .filter((cat) => selected.includes(cat.id))
                                                .map((cat) => cat.category_name || cat.type)
                                                .join(", "),
                                        MenuProps: {
                                            PaperProps: {
                                                sx: { maxHeight: 250 },
                                            },
                                        },
                                    }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <CategoryIcon />
                                            </InputAdornment>
                                        ),
                                    }}
                                    error={!!errors.scrap_item}
                                    helperText={errors.scrap_item?.message}
                                >
                                    <MenuItem value="all">
                                        <em>{isAllSelected ? "Deselect All" : "Select All"}</em>
                                    </MenuItem>
                                    {categories.map((category) => (
                                        <MenuItem key={category.id} value={category.id}>
                                            {category.category_name || category.type} - {kg(category.scrap_weight)} kg
                                            {category.note ? ` (${category.note})` : ""}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            );
                        }}
                    />

                    {/* Auto-filled Weight */}
                    <Controller
                        name="weight"
                        control={control}
                        rules={{ required: "Weight is required" }}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                label="Scrap Weight (kg)"
                                fullWidth
                                type="number"
                                margin="normal"
                                InputProps={{ readOnly: true }}
                                error={!!errors.weight}
                                helperText={errors.weight?.message}
                            />
                        )}
                    />

                    {/* Amount */}
                    <Controller
                        name="amount"
                        control={control}
                        rules={{
                            required: "Amount is required",
                            validate: (v) => Number(v) > 0 || "Enter an amount above 0",
                        }}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                label="Amount (₹)"
                                fullWidth
                                type="number"
                                inputProps={{ min: 0.01, step: 0.01 }}
                                margin="normal"
                                error={!!errors.amount}
                                helperText={errors.amount?.message}
                            />
                        )}
                    />

                    {/* Payment mode - owner Q7 */}
                    <Controller
                        name="payment_mode"
                        control={control}
                        rules={{ required: "Choose how the scrap was paid" }}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                select
                                label="Payment Mode"
                                fullWidth
                                margin="normal"
                                error={!!errors.payment_mode}
                                helperText={errors.payment_mode?.message}
                            >
                                <MenuItem value="Cash">Cash</MenuItem>
                                <MenuItem value="Online">Online</MenuItem>
                            </TextField>
                        )}
                    />

                    {/* Buyer - owner Q7 (optional) */}
                    <Controller
                        name="buyer_name"
                        control={control}
                        rules={{ maxLength: { value: 150, message: "At most 150 characters" } }}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                label="Buyer (optional)"
                                fullWidth
                                margin="normal"
                                inputProps={{ maxLength: 150 }}
                                error={!!errors.buyer_name}
                                helperText={errors.buyer_name?.message}
                            />
                        )}
                    />

                    {/* Date */}
                    <Controller
                        name="date"
                        control={control}
                        rules={{
                            required: "Date is required",
                            validate: (v) => v <= todayIndia() || "The sale date cannot be after today",
                        }}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                label="Date"
                                fullWidth
                                type="date"
                                margin="normal"
                                inputProps={{ max: todayIndia() }}
                                InputLabelProps={{ shrink: true }}
                                error={!!errors.date}
                                helperText={errors.date?.message}
                            />
                        )}
                    />
                </form>
            </DialogContent>

            <DialogActions>
                <FormButton func={handleSubmit(onSubmit)} title="Submit" />
            </DialogActions>
        </Dialog>
    );
};

export default SellScrapModal;
